import {ABILITY_CAST_COLORS as COLORS, ABILITY_CAST_TEXTURE_KEYS} from '../../configs/abilityCastAnimation';
import {MELEE_TEXTURE_KEYS} from '../../configs/meleeAnimation';
import {UNIT_DEATH_CAST, UNIT_STARVATION_CAST} from '../../configs/unitDeathAnimation';
import {
  clamp01, createElement, easeOutBack, easeOutQuad, fadeInOut, lerp, mixColor,
} from './buildAbilityCast';
import {pushPuff} from './buildCombatCast';
import {buildUnitDeathCastPlan} from './buildUnitDeathCast';

const TWO_PI = Math.PI * 2;

/** Цвет колоса по доле времени тления */
export const resolveRottingWheatTint = (share) => {
  const {tints} = UNIT_STARVATION_CAST.wheat;
  const nextIndex = tints.findIndex((stop) => stop.share >= share);
  if (nextIndex <= 0) {
    return tints[nextIndex === 0 ? 0 : tints.length - 1].tint;
  }
  const from = tints[nextIndex - 1];
  const to = tints[nextIndex];
  return mixColor(from.tint, to.tint, (share - from.share) / (to.share - from.share));
};

// Мелкая частица: летит с места from и гаснет
const pushParticle = (elements, {textureKey = ABILITY_CAST_TEXTURE_KEYS.PUFF, tint, sizePx, startMs, lifeMs, order, blend, pose}) => {
  elements.push(createElement({textureKey, tint, sizePx, startMs, durationMs: lifeMs, order, blend, pose}));
};

/** Колос над отрядом: золотой и светится, затем тлеет, чернеет, вянет и разлетается */
const pushWheat = (elements, {center, size, startMs, witherAtMs, impactMs, rng}) => {
  const {goldMs, wheat, glow, ember, flakes, burst} = UNIT_STARVATION_CAST;
  const at = {x: center.x, y: center.y - size * wheat.liftFactor};
  const rotAtMs = startMs + goldMs;
  const rotMs = witherAtMs - rotAtMs;
  const endMs = impactMs + wheat.fadeMs;
  const durationMs = endMs - startMs;

  const wheatPose = (local) => {
    const elapsedMs = startMs + local * durationMs;
    const rot = clamp01((elapsedMs - rotAtMs) / rotMs);
    const popped = clamp01((elapsedMs - startMs) / wheat.popMs);
    const fade = clamp01((elapsedMs - impactMs) / wheat.fadeMs);
    return {
      x: at.x + Math.sin(rot * Math.PI * wheat.tremorTurns) * size * wheat.tremorFactor * rot,
      y: at.y + size * wheat.sinkFactor * rot * rot,
      scale: lerp(wheat.popFromScale, 1, Math.max(0, easeOutBack(popped))) * lerp(1, wheat.wiltScale, rot),
      rotation: wheat.wiltTilt * rot * rot,
      alpha: 1 - fade,
      rot,
    };
  };
  elements.push(createElement({
    textureKey: wheat.textureKey, tint: wheat.outline, sizePx: size * wheat.sizeFactor, startMs, durationMs, order: 10,
    pose: (local) => {
      const {rot, ...pose} = wheatPose(local);
      return {...pose, scale: pose.scale * wheat.outlineScale};
    },
  }));
  elements.push(createElement({
    textureKey: wheat.textureKey, sizePx: size * wheat.sizeFactor, startMs, durationMs, order: 10.5,
    pose: (local) => {
      const {rot, ...pose} = wheatPose(local);
      return {...pose, tint: resolveRottingWheatTint(rot)};
    },
  }));

  // СВЕТ: сияет, пока колос золотой, и гаснет, когда он начинает тлеть
  const lightMs = goldMs + rotMs * ember.startShare;
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: glow.tint, sizePx: size * glow.sizeFactor, startMs, durationMs: lightMs, order: 9, blend: 'add',
    pose: (local) => ({
      ...at,
      scale: lerp(0.5, 1, easeOutQuad(clamp01(local * 3))),
      alpha: glow.alpha * fadeInOut(local, 0.15, 0.75) * (0.8 + 0.2 * Math.sin(local * Math.PI * 2 * glow.pulses)),
    }),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint: glow.tint, sizePx: size * glow.raysSizeFactor, startMs, durationMs: lightMs, order: 8.5, blend: 'add',
    pose: (local) => ({
      ...at,
      scale: lerp(0.4, 1, easeOutQuad(clamp01(local * 3))),
      alpha: glow.raysAlpha * fadeInOut(local, 0.15, 0.7),
      rotation: glow.raysTurn * local,
    }),
  }));

  // ТЛЕНИЕ: багровый свет под колосом дрожит, уходят угли и дым
  const emberStartMs = rotAtMs + rotMs * ember.startShare;
  const emberMs = rotMs * (ember.endShare - ember.startShare);
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: ember.tint, sizePx: size * ember.sizeFactor, startMs: emberStartMs, durationMs: emberMs, order: 9.5, blend: 'add',
    pose: (local) => ({
      ...at,
      alpha: ember.alpha * fadeInOut(local, 0.2, 0.7) * (0.65 + 0.35 * Math.sin(local * Math.PI * 2 * ember.flickers)),
    }),
  }));
  for (let index = 0; index < ember.sparks.count; index += 1) {
    const spark = ember.sparks;
    const shiftX = (rng() - 0.5) * 2 * size * spark.spreadFactor;
    const fromX = at.x + (rng() - 0.5) * size * spark.spreadFactor;
    const riseY = size * spark.riseFactor * lerp(0.6, 1.2, rng());
    pushParticle(elements, {
      tint: spark.tints[index % spark.tints.length], sizePx: size * spark.sizeFactor,
      startMs: emberStartMs + emberMs * (index / spark.count), lifeMs: spark.lifeMs, order: 11, blend: 'add',
      pose: (local) => ({
        x: fromX + shiftX * easeOutQuad(local),
        y: at.y - riseY * easeOutQuad(local),
        scale: lerp(1, 0.4, local),
        alpha: fadeInOut(local, 0.1, 0.5),
      }),
    });
  }
  for (let index = 0; index < ember.smoke.count; index += 1) {
    const smoke = ember.smoke;
    pushPuff(elements, {
      at, tint: smoke.tint, sizePx: size * smoke.sizeFactor * lerp(0.8, 1.2, rng()), startMs: emberStartMs + emberMs * (index / smoke.count),
      lifeMs: smoke.lifeMs, alpha: smoke.alpha, shiftX: (rng() - 0.5) * 2 * size * smoke.spreadFactor, riseY: size * smoke.riseFactor,
      spin: (rng() - 0.5) * 0.9, order: 9.2,
    });
  }

  // ТРУХА осыпается с колоса вниз: чем дальше, тем чернее
  for (let index = 0; index < flakes.count; index += 1) {
    const share = index / flakes.count;
    const shiftX = (rng() - 0.5) * 2 * size * flakes.spreadFactor;
    const fallY = size * flakes.fallFactor * lerp(0.75, 1.15, rng());
    const spin = (rng() - 0.5) * TWO_PI;
    pushParticle(elements, {
      tint: flakes.tints[Math.min(flakes.tints.length - 1, Math.floor(share * flakes.tints.length))], sizePx: size * flakes.sizeFactor,
      // Сначала по чуть-чуть, к концу все гуще
      startMs: emberStartMs + emberMs * Math.sqrt(share), lifeMs: flakes.lifeMs, order: 9.8,
      pose: (local) => ({
        x: at.x + shiftX * easeOutQuad(local),
        y: at.y + fallY * local * local,
        scale: lerp(1, 0.6, local),
        alpha: fadeInOut(local, 0.05, 0.65),
        rotation: spin * local,
      }),
    });
  }

  // Последнее: то, что осталось от колоса, разлетается и падает
  for (let index = 0; index < burst.count; index += 1) {
    const angle = index / burst.count * TWO_PI + (rng() - 0.5) * 0.4;
    const reach = size * burst.reachFactor * lerp(0.5, 1.1, rng());
    pushParticle(elements, {
      tint: burst.tints[index % burst.tints.length], sizePx: size * burst.sizeFactor * lerp(0.7, 1.3, rng()), startMs: impactMs, lifeMs: burst.lifeMs, order: 11.5,
      pose: (local) => ({
        x: at.x + Math.cos(angle) * reach * easeOutQuad(local),
        y: at.y + Math.sin(angle) * reach * easeOutQuad(local) + size * burst.fallFactor * local * local,
        scale: lerp(1, 0.5, local),
        alpha: fadeInOut(local, 0.04, 0.6),
      }),
    });
  }
};

/**
 * План гибели от голода: колос над отрядом сгнивает, отряд чахнет, а затем гибнет так же, как
 * в бою (см. buildUnitDeathCastPlan). Настоящий отряд убирается с поля, когда план запущен:
 * двойник стоит на месте с самого начала.
 *
 * @param {Object} params — как у buildUnitDeathCastPlan
 */
export const buildUnitStarvationCastPlan = ({
  unit, hexSize, color, art, tier = 1, delayMs = 0, isReduced = false, hexRotation = 0, rng = Math.random,
}) => {
  const {goldMs, witherMs, unit: withered, dust} = UNIT_STARVATION_CAST;
  const {collapseMs, unit: deathPose} = UNIT_DEATH_CAST;
  const size = hexSize;
  const {center, body, ground} = unit;
  const witherStartMs = delayMs + goldMs;
  const witherAtMs = delayMs + witherMs;
  // Сама гибель рисуется без двойника: он здесь свой, и вместо белой вспышки сереет
  const plan = buildUnitDeathCastPlan({
    unit, hexSize, color, art: null, tier, delayMs: witherAtMs, isReduced, hexRotation, rng,
  });
  const {impactMs} = plan;
  const elements = [...plan.elements];

  if (art?.image) {
    const side = rng() < 0.5 ? -1 : 1;
    const doublePose = (elapsedMs) => {
      if (elapsedMs < impactMs) {
        const share = clamp01((elapsedMs - witherStartMs) / (witherAtMs - witherStartMs));
        return {
          x: center.x + Math.sin(share * Math.PI * withered.tremorTurns) * size * withered.tremorFactor * share,
          y: center.y + size * withered.droopFactor * share,
          scale: lerp(1, withered.witherScale, easeOutQuad(share)),
          alpha: 1,
          witherShare: share,
          ashShare: 0,
        };
      }
      const share = clamp01((elapsedMs - impactMs) / collapseMs);
      return {
        x: center.x,
        y: center.y + size * (withered.droopFactor + deathPose.sinkFactor * share * share),
        scale: lerp(withered.witherScale, deathPose.endScale * withered.witherScale, easeOutQuad(share)),
        rotation: side * deathPose.tilt * share * share,
        alpha: 1 - share * share,
        witherShare: 1,
        ashShare: clamp01(share * 2.5),
      };
    };
    const doubleMs = impactMs + collapseMs;
    if (art.outline) {
      elements.push(createElement({
        textureKey: art.outline.textureKey, sizePx: art.outline.sizePx, startMs: 0, durationMs: doubleMs, order: 4,
        pose: (local, elapsedMs) => {
          const {witherShare, ashShare, ...pose} = doublePose(elapsedMs);
          return {...pose, tint: mixColor(mixColor(COLORS.white, withered.withered, witherShare), deathPose.ash, ashShare)};
        },
      }));
    }
    elements.push(createElement({
      textureKey: art.image.textureKey, tint: art.tint, sizePx: art.image.sizePx, startMs: 0, durationMs: doubleMs, order: 5,
      pose: (local, elapsedMs) => {
        const {witherShare, ashShare, ...pose} = doublePose(elapsedMs);
        return {...pose, tint: mixColor(mixColor(art.tint, withered.withered, witherShare), deathPose.ash, ashShare)};
      },
    }));
  }

  pushWheat(elements, {center, size, startMs: delayMs, witherAtMs, impactMs, rng});

  // Сухая пыль поднимается из-под отряда, пока он чахнет
  if (!isReduced) {
    for (let index = 0; index < dust.count; index += 1) {
      const angle = index / dust.count * TWO_PI + (rng() - 0.5) * 0.7;
      pushPuff(elements, {
        at: {x: body.x, y: ground.y}, tint: dust.tint, sizePx: size * dust.sizeFactor,
        startMs: witherStartMs + (witherAtMs - witherStartMs) * (dust.startShare + index / dust.count * (1 - dust.startShare)),
        lifeMs: dust.lifeMs, alpha: dust.alpha,
        shiftX: Math.cos(angle) * size * dust.spreadFactor, riseY: size * dust.riseFactor, spin: (rng() - 0.5) * 0.9, order: 2,
      });
    }
  }

  return {
    ...plan,
    elements,
    totalMs: elements.reduce((latest, element) => Math.max(latest, element.endMs), plan.totalMs),
  };
};
